import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Errors } from "../../constants/errors";
import {
  INSTAGRAM_GET_LOADING,
  INSTAGRAM_POST_LOADING,
  INSTAGRAM_SYNC_LOADING,
} from "../../constants/loading.constant";
import { useGlobalCore } from "../globals";
import { Media } from "../socialPost/interfaces/api/get-social-posts";
import { PostImage } from "../socialPost/interfaces/postImage";
import { PostVideo } from "../socialPost/interfaces/postVideo";
import { InstagramAccount } from "./interface/api/get-instagram-accounts";
import { PostInstagramBindCodeRequest } from "./interface/api/post-instagram-bind";
import { instagramApiService } from "./services/api";
import {
  getInstagramAccountsFailureAction,
  getInstagramAccountsInitAction,
  getInstagramAccountsSuccessAction,
  getInstagramCodeFailureAction,
  getInstagramCodeInitAction,
  getInstagramCodeSuccessAction,
  getInstagramFailureAction,
  getInstagramInitAction,
  getInstagramSuccessAction,
  postBindInstagramFailureAction,
  postBindInstagramInitAction,
  postBindInstagramSuccessAction,
  postInstagramCodeFailureAction,
  postInstagramCodeInitAction,
  postInstagramSocialPostFailureAction,
  postInstagramSocialPostInitAction,
  postInstagramSocialPostSuccessAction,
  postInstagramSocialStoryFailureAction,
  postInstagramSocialStoryInitAction,
  postInstagramSocialStorySuccessAction,
  postUnbindInstagramFailureAction,
  postUnbindInstagramInitAction,
  postUnbindInstagramSuccessAction,
  putInstagramSocialPostFailureAction,
  putInstagramSocialPostInitAction,
  putInstagramSocialPostSuccessAction,
  putInstagramSocialStoryFailureAction,
  putInstagramSocialStoryInitAction,
  putInstagramSocialStorySuccessAction,
} from "./store/api-actions";
import { useIntl } from "react-intl";
import {
  accountsSelector,
  facebookProfileNameSelector,
  errorCodeSelector,
  errorMessageSelector,
  settingSelector,
} from "./store/selector";
import { dispatchOptimisticScheduledPostIfNeeded, dispatchOptimisticVideoUploadPost } from "../socialPost/helpers/scheduleResponseToSocialPost";
import { Mentions } from "../socialPost/interfaces/Mention";
import { cleanErrorsAction } from "./store/actions";
import { getInstagramMenssegerSuccessAction, postUnbindInstagramMenssegerSuccessAction } from "../instagramMensseger/store/api-actions";
import {
  bumpInstagramSettingsGeneration,
  isStaleInstagramSettingsRequest,
} from "./settingsGeneration";

export function useInstagramCore() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const settings = useSelector(settingSelector);
  const accounts = useSelector(accountsSelector);
  const facebookProfileName = useSelector(facebookProfileNameSelector);
  const errorCode = useSelector(errorCodeSelector);
  const errorMessage = useSelector(errorMessageSelector);
  const { turnOnLoading, turnOffLoading } = useGlobalCore();

  const notifyInstagramQueued = useCallback(
    (_scheduleDate: string) => {
      // The composer owns the user-facing success message so users see one
      // reassuring confirmation instead of provider-specific pending notices.
    },
    []
  );

  const unbind = useCallback(
    async (companyId: string, onSuccess?: () => void) => {
      bumpInstagramSettingsGeneration();
      try {
        dispatch(postUnbindInstagramInitAction());
        await instagramApiService.unbind(companyId);
        dispatch(postUnbindInstagramSuccessAction());
        dispatch(postUnbindInstagramMenssegerSuccessAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        dispatch(postUnbindInstagramFailureAction({ error }));
        const message =
          error.response?.data?.message ||
          intl.formatMessage({ id: "app.toast.error26" });
        toast.error(message);
      }
    },
    [dispatch, intl]
  );

  const bind = useCallback(
    async (
      companyId: string,
      account: InstagramAccount,
      password: string,
      token: string,
      onSuccess?: () => void
    ) => {
      const generation = bumpInstagramSettingsGeneration();
      try {
        dispatch(postBindInstagramInitAction());
        turnOnLoading(INSTAGRAM_SYNC_LOADING);
        const bindResponse = await instagramApiService.bind(companyId, {
          userName: account.userName || account.name || "instagram",
          password,
          token,
          accountId: account.id,
          pageToken: account.token,
        });
        if (isStaleInstagramSettingsRequest(generation)) return;
        dispatch(postBindInstagramSuccessAction({ settings: bindResponse.setting }));
        if (bindResponse.messengerSetting) {
          dispatch(
            getInstagramMenssegerSuccessAction({
              settings: bindResponse.messengerSetting,
            })
          );
        }
        turnOffLoading(INSTAGRAM_SYNC_LOADING);
        toast.success(intl.formatMessage({ id: "app.toast.sucess12" }));
        if (bindResponse.messengerError) {
          toast.warn(bindResponse.messengerError.message);
        }
        await onSuccess?.();
      } catch (error: any) {
        if (error.request?.status === 403) {
          const code: number = JSON.parse(error.request.response).code || 0;
          const message: string =
            JSON.parse(error.request.response).message ||
            "Ingrese el código enviado al telefono de verificación";
          switch (code) {
            case Errors.Checkpoint:
              dispatch(
                postBindInstagramFailureAction({
                  errorCode: Errors.Checkpoint,
                  errorMessage: message,
                })
              );
              break;
            case Errors["2FA"]:
              dispatch(
                postBindInstagramFailureAction({
                  errorCode: Errors["2FA"],
                  errorMessage: message,
                })
              );
              break;
            case Errors.MessageDissabled:
            case Errors.MessageNotAllowed:
            case Errors.NotBusinessAccount:
              dispatch(
                postBindInstagramFailureAction({
                  errorCode: code,
                  errorMessage: message,
                })
              );
              toast.error(message);
              break;
            default:
              toast.error(intl.formatMessage({ id: "app.toast.error26" }));
              console.error(error, code);
          }
        } else {
          const responseBody =
            error.response?.data ||
            (error.request?.response
              ? JSON.parse(error.request.response)
              : null);
          const message =
            responseBody?.message ||
            intl.formatMessage({ id: "app.toast.error26" });
          toast.error(message);
          console.error(error);
        }
        turnOffLoading(INSTAGRAM_SYNC_LOADING);
      }
    },
    [dispatch, turnOffLoading, turnOnLoading, intl]
  );

  const get = useCallback(
    async (companyId: string) => {
      const generation = bumpInstagramSettingsGeneration();
      try {
        dispatch(getInstagramInitAction());
        turnOnLoading(INSTAGRAM_GET_LOADING);
        const settings = await instagramApiService.get(companyId);
        if (isStaleInstagramSettingsRequest(generation)) return;
        dispatch(getInstagramSuccessAction({ settings }));
        turnOffLoading(INSTAGRAM_GET_LOADING, 1500);
      } catch (error: any) {
        turnOffLoading(INSTAGRAM_GET_LOADING);
        console.error(error);
        if (!isStaleInstagramSettingsRequest(generation)) {
          dispatch(getInstagramFailureAction({ error }));
        }
      }
    },
    [dispatch, turnOffLoading, turnOnLoading]
  );

  const getAccounts = useCallback(
    async (companyId: string, token: string) => {
      try {
        dispatch(getInstagramAccountsInitAction());
        turnOnLoading(INSTAGRAM_SYNC_LOADING);
        const { accounts, facebookProfileName } =
          await instagramApiService.accounts(companyId, token);
        dispatch(
          getInstagramAccountsSuccessAction({ accounts, facebookProfileName })
        );
        turnOffLoading(INSTAGRAM_SYNC_LOADING, 500);
      } catch (error: any) {
        turnOffLoading(INSTAGRAM_SYNC_LOADING);
        toast.error(intl.formatMessage({ id: "app.toast.error27" }));
        console.error(error);
        dispatch(getInstagramAccountsFailureAction({ error }));
      }
    },
    [dispatch, turnOffLoading, turnOnLoading, intl]
  );

  const socialPost = useCallback(
    async (
      companyId: string,
      text: string,
      mentions: Mentions[],
      images: PostImage[],
      scheduleDate: string,
      repostId: string,
      onSuccess?: () => void
    ) => {
      try {
        dispatch(postInstagramSocialPostInitAction());
        turnOnLoading(INSTAGRAM_POST_LOADING);
        const data = await instagramApiService.socialPost(
          text,
          mentions,
          images,
          scheduleDate,
          repostId,
          companyId
        );
        dispatch(postInstagramSocialPostSuccessAction());
        turnOffLoading(INSTAGRAM_POST_LOADING);
        notifyInstagramQueued(scheduleDate);
        dispatchOptimisticScheduledPostIfNeeded(dispatch, data, {
          scheduleDate,
          companyId,
          defaultProvider: "instagram",
          postType: images.length > 0 ? "image" : "text",
          text,
        });
        onSuccess?.();
      } catch (error: any) {    
        if (error.request.status === 403) {
          const code: number = JSON.parse(error.request.response).code || 0;
          const message: string = JSON.parse(error.request.response).message;
          switch (code) {
            case Errors.NotBusinessAccount:
              dispatch(
                postInstagramSocialPostFailureAction({
                  errorCode: Errors.NotBusinessAccount,
                  errorMessage: message,
                })
              );
              break;
          }
          toast.error(
            scheduleDate
              ? intl.formatMessage({ id: "app.toast.error29" })
              : intl.formatMessage({ id: "app.toast.error30" })
          );
          console.error(error, code);
        } else {
          toast.error(
            scheduleDate
              ? intl.formatMessage({ id: "app.toast.error29" })
              : intl.formatMessage({ id: "app.toast.error30" })
          );
          console.error(error);
        }
        turnOffLoading(INSTAGRAM_POST_LOADING);
      }
    },
    [dispatch, turnOffLoading, turnOnLoading, intl, notifyInstagramQueued]
  );

  const socialPostVideo = useCallback(
    async (
      companyId: string,
      text: string,
      mentions: Mentions[],
      video: PostVideo,
      scheduleDate: string,
      repostId: string,
      onSuccess?: () => void
    ) => {
      const progressToastId = "social-video-upload-progress-ig";
      try {
        dispatch(postInstagramSocialPostInitAction());
        turnOnLoading(INSTAGRAM_POST_LOADING);
        toast.info("Subiendo video… 0%", {
          toastId: progressToastId,
          autoClose: false,
        });
        const data = await instagramApiService.socialPostVideo(
          text,
          mentions,
          video,
          scheduleDate,
          repostId,
          companyId,
          (progress) => {
            if (progress.phase === "uploading") {
              toast.update(progressToastId, {
                render: `Subiendo video… ${progress.percent ?? 0}%`,
                type: "info",
                autoClose: false,
              });
            } else if (progress.phase === "completing") {
              toast.update(progressToastId, {
                render: "Procesando publicación…",
                type: "info",
                autoClose: false,
              });
            }
          },
          (payload) => {
            dispatchOptimisticVideoUploadPost(dispatch, payload, {
              scheduleDate,
              companyId,
              defaultProvider: "instagram",
              text,
            });
          }
        );
        dispatch(postInstagramSocialPostSuccessAction());
        turnOffLoading(INSTAGRAM_POST_LOADING);
        toast.dismiss(progressToastId);
        notifyInstagramQueued(scheduleDate);
        dispatchOptimisticScheduledPostIfNeeded(dispatch, data, {
          scheduleDate,
          companyId,
          defaultProvider: "instagram",
          postType: "video",
          text,
        });
        onSuccess?.();
      } catch (error: any) {
        toast.dismiss(progressToastId);
        if (error.request?.status === 403) {
          const code: number = JSON.parse(error.request.response).code || 0;
          const message: string = JSON.parse(error.request.response).message;
          switch (code) {
            case Errors.NotBusinessAccount:
              dispatch(
                postInstagramSocialPostFailureAction({
                  errorCode: Errors.NotBusinessAccount,
                  errorMessage: message,
                })
              );
              break;
          }
          toast.error(
            scheduleDate
              ? intl.formatMessage({ id: "app.toast.error29" })
              : intl.formatMessage({ id: "app.toast.error30" })
          );
          console.error(error, code);
        } else {
          toast.error(
            scheduleDate
              ? intl.formatMessage({ id: "app.toast.error29" })
              : intl.formatMessage({ id: "app.toast.error30" })
          );
          console.error(error);
        }
        turnOffLoading(INSTAGRAM_POST_LOADING);
      }
    },
    [dispatch, turnOffLoading, turnOnLoading, intl, notifyInstagramQueued]
  );

  const socialStory = useCallback(
    async (
      companyId: string,
      text: string,
      images: PostImage[],
      scheduleDate: string,
      onSuccess?: () => void
    ) => {
      try {
        dispatch(postInstagramSocialStoryInitAction());
        turnOnLoading(INSTAGRAM_POST_LOADING);
        await instagramApiService.socialStory(
          text,
          images,
          scheduleDate,
          companyId
        );
        dispatch(postInstagramSocialStorySuccessAction());
        turnOffLoading(INSTAGRAM_POST_LOADING);
        toast.success(intl.formatMessage({ id: "app.toast.sucess13" }));
        onSuccess?.();
      } catch (error: any) {
        turnOffLoading(INSTAGRAM_POST_LOADING);
        toast.error(intl.formatMessage({ id: "app.toast.error29" }));
        console.error(error);
        dispatch(postInstagramSocialStoryFailureAction({ error }));
      }
    },
    [dispatch, turnOffLoading, turnOnLoading, intl]
  );

  const socialStoryVideo = useCallback(
    async (
      companyId: string,
      text: string,
      video: PostVideo,
      scheduleDate: string,
      onSuccess?: () => void
    ) => {
      try {
        dispatch(postInstagramSocialStoryInitAction());
        turnOnLoading(INSTAGRAM_POST_LOADING);
        await instagramApiService.socialStoryVideo(
          text,
          video,
          scheduleDate,
          companyId
        );
        dispatch(postInstagramSocialStorySuccessAction());
        turnOffLoading(INSTAGRAM_POST_LOADING);
        toast.success(intl.formatMessage({ id: "app.toast.sucess13" }));
        onSuccess?.();
      } catch (error: any) {
        turnOffLoading(INSTAGRAM_POST_LOADING);
        toast.error(intl.formatMessage({ id: "app.toast.error29" }));
        console.error(error);
        dispatch(postInstagramSocialStoryFailureAction({ error }));
      }
    },
    [dispatch, turnOffLoading, turnOnLoading, intl]
  );

  const requestCode = useCallback(
    async (companyId: string, codeType: string) => {
      try {
        dispatch(getInstagramCodeInitAction());
        await instagramApiService.getCode(companyId, codeType);
        dispatch(getInstagramCodeSuccessAction());
      } catch (error: any) {
        console.error(error);
        dispatch(getInstagramCodeFailureAction({ error }));
      }
    },
    [dispatch]
  );

  const sendCode = useCallback(
    async (companyId: string, data: PostInstagramBindCodeRequest) => {
      try {
        turnOnLoading(INSTAGRAM_SYNC_LOADING);
        dispatch(postInstagramCodeInitAction());
        const settings = await instagramApiService.postCode(companyId, data);
        dispatch(postBindInstagramSuccessAction({ settings }));
        turnOffLoading(INSTAGRAM_SYNC_LOADING, 500);
        toast.success(intl.formatMessage({ id: "app.toast.sucess12" }));
      } catch (error: any) {
        console.error(error);
        dispatch(postInstagramCodeFailureAction({ error }));
      }
    },
    [dispatch, turnOffLoading, turnOnLoading, intl]
  );

  const modifySocialPost = useCallback(
    async (
      companyId: string,
      postId: string,
      text: string,
      mentions: Mentions[],
      images: PostImage[],
      scheduleDate: string,
      media: Media[],
      onSuccess?: () => void
    ) => {
      try {
        dispatch(putInstagramSocialPostInitAction());
        turnOnLoading(INSTAGRAM_POST_LOADING);
        await instagramApiService.putSocialPost(
          postId,
          text,
          mentions,
          images,
          scheduleDate,
          companyId,
          media
        );
        dispatch(putInstagramSocialPostSuccessAction());
        turnOffLoading(INSTAGRAM_POST_LOADING);
        notifyInstagramQueued(scheduleDate);
        onSuccess?.();
      } catch (error: any) {
        if (error.request.status === 403) {
          const code: number = JSON.parse(error.request.response).code || 0;
          const message: string = JSON.parse(error.request.response).message;
          switch (code) {
            case Errors.NotBusinessAccount:
              dispatch(
                putInstagramSocialPostFailureAction({
                  errorCode: Errors.NotBusinessAccount,
                  errorMessage: message,
                })
              );
              break;
          }
          toast.error(
            scheduleDate
              ? intl.formatMessage({ id: "app.toast.error29" })
              : intl.formatMessage({ id: "app.toast.error30" })
          );
          console.error(error, code);
        } else {
          toast.error(
            scheduleDate
              ? intl.formatMessage({ id: "app.toast.error29" })
              : intl.formatMessage({ id: "app.toast.error30" })
          );
          console.error(error);
        }
        turnOffLoading(INSTAGRAM_POST_LOADING);
      }
    },
    [dispatch, turnOffLoading, turnOnLoading, intl, notifyInstagramQueued]
  );

  const modifySocialPostVideo = useCallback(
    async (
      companyId: string,
      postId: string,
      text: string,
      mentions: Mentions[],
      video: PostVideo | null,
      scheduleDate: string,
      media: Media[],
      onSuccess?: () => void
    ) => {
      try {
        dispatch(putInstagramSocialPostInitAction());
        turnOnLoading(INSTAGRAM_POST_LOADING);
        await instagramApiService.putSocialPostVideo(
          postId,
          text,
          mentions,
          video,
          scheduleDate,
          companyId,
          media
        );
        dispatch(putInstagramSocialPostSuccessAction());
        turnOffLoading(INSTAGRAM_POST_LOADING);
        notifyInstagramQueued(scheduleDate);
        onSuccess?.();
      } catch (error: any) {
        if (error.request.status === 403) {
          const code: number = JSON.parse(error.request.response).code || 0;
          const message: string = JSON.parse(error.request.response).message;
          switch (code) {
            case Errors.NotBusinessAccount:
              dispatch(
                putInstagramSocialPostFailureAction({
                  errorCode: Errors.NotBusinessAccount,
                  errorMessage: message,
                })
              );
              break;
          }
          toast.error(
            scheduleDate
              ? intl.formatMessage({ id: "app.toast.error29" })
              : intl.formatMessage({ id: "app.toast.error30" })
          );
          console.error(error, code);
        } else {
          toast.error(
            scheduleDate
              ? intl.formatMessage({ id: "app.toast.error29" })
              : intl.formatMessage({ id: "app.toast.error30" })
          );
          console.error(error);
        }
        turnOffLoading(INSTAGRAM_POST_LOADING);
      }
    },
    [dispatch, turnOffLoading, turnOnLoading, intl, notifyInstagramQueued]
  );

  const modifySocialStory = useCallback(
    async (
      companyId: string,
      storyId: string,
      text: string,
      images: PostImage[],
      scheduleDate: string,
      media: Media[],
      onSuccess?: () => void
    ) => {
      try {
        dispatch(putInstagramSocialStoryInitAction());
        turnOnLoading(INSTAGRAM_POST_LOADING);
        await instagramApiService.putSocialStory(
          storyId,
          text,
          images,
          scheduleDate,
          media,
          companyId
        );
        dispatch(putInstagramSocialStorySuccessAction());
        turnOffLoading(INSTAGRAM_POST_LOADING);
        toast.success(intl.formatMessage({ id: "app.toast.sucess13" }));
        onSuccess?.();
      } catch (error: any) {
        turnOffLoading(INSTAGRAM_POST_LOADING);
        toast.error(intl.formatMessage({ id: "app.toast.error29" }));
        console.error(error);
        dispatch(putInstagramSocialStoryFailureAction({ error }));
      }
    },
    [dispatch, turnOffLoading, turnOnLoading, intl]
  );

  const modifySocialStoryVideo = useCallback(
    async (
      companyId: string,
      storyId: string,
      text: string,
      video: PostVideo | null,
      scheduleDate: string,
      media: Media[],
      onSuccess?: () => void
    ) => {
      try {
        dispatch(putInstagramSocialStoryInitAction());
        turnOnLoading(INSTAGRAM_POST_LOADING);
        await instagramApiService.putSocialStoryVideo(
          storyId,
          text,
          video,
          scheduleDate,
          media,
          companyId
        );
        dispatch(putInstagramSocialStorySuccessAction());
        turnOffLoading(INSTAGRAM_POST_LOADING);
        toast.success(intl.formatMessage({ id: "app.toast.sucess13" }));
        onSuccess?.();
      } catch (error: any) {
        turnOffLoading(INSTAGRAM_POST_LOADING);
        toast.error(intl.formatMessage({ id: "app.toast.error29" }));
        console.error(error);
        dispatch(putInstagramSocialStoryFailureAction({ error }));
      }
    },
    [dispatch, turnOffLoading, turnOnLoading, intl]
  );

  const cleanErrors = useCallback(() => dispatch(cleanErrorsAction()), [dispatch]);

  return {
    settings,
    errorCode,
    errorMessage,
    unbind,
    bind,
    get,
    socialPost,
    socialPostVideo,
    requestCode,
    sendCode,
    getAccounts,
    accounts,
    facebookProfileName,
    modifySocialPost,
    modifySocialPostVideo,
    socialStory,
    socialStoryVideo,
    modifySocialStory,
    modifySocialStoryVideo,
    cleanErrors,
  };
}
